import React, { useEffect, useState } from "react";
import MainComponent from "./main/main";
import { Page } from "./types/page";
import { ApiEndpoint, apiService } from "./lib/api";
import shadow from "react-shadow";
import styles from "./app.css?inline";
import { cn } from "./lib/utils";
import { Button } from "./components/ui/button";
import { X } from "lucide-react";
import { renderIntegrations } from "./integrations";

type WidgetOptions = {
  color: {
    primary: string;
    secondary: string;
  };
};

interface DayscheduleWidgetProps extends React.HTMLAttributes<HTMLElement> {
  url?: string;
  options?: string;
  popup?: boolean;
  onClose?: () => void; // optional callback
}

// --- Tailwind normalizer for ShadowRoot ---
function normalizeTailwind(css: string): string {
  return css
    .replace(/:root\b/g, ":host")
    .replaceAll("((-webkit-hyphens:none)) and ", "")
    .replaceAll("(-webkit-hyphens: none) and ", "");
}

export const DayscheduleWidget = ({
  url,
  options,
  popup,
  onClose,
}: DayscheduleWidgetProps) => {
  const [config, setConfig] = useState<WidgetOptions | null>(null);
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [colorMode, setColorMode] = useState<string | null>("system");
  const [colorTheme, setColorTheme] = useState<string | null>("theme-default");

  useEffect(() => {
    if (page?.color?.mode) {
      if (page.color.mode === "system") {
        const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
          .matches
          ? "dark"
          : "light";
        setColorMode(`${systemTheme}`);
      } else {
        setColorMode(`${page.color.mode}`);
      }
    }

    if (page?.color?.theme) {
      if (page.color.theme.endsWith("-scaled")) {
        setColorTheme(`theme-${page.color.theme} theme-scaled`);
      } else {
        setColorTheme(`theme-${page.color.theme}`);
      }
    }
  }, [page?.color]);

  useEffect(() => {
    if (options) {
      try {
        const parsed = JSON.parse(options);
        setConfig(parsed);
      } catch (e) {
        console.error("Invalid options JSON", e);
      }
    }
  }, [options]);

  const fetchPage = async (subdomain: string | null) => {
    if (!subdomain) {
      setError("Invalid page url");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await apiService.get(ApiEndpoint.pages, subdomain);
      if (!data) {
        setError("Page not found");
      } else {
        setPage(data);
      }
    } catch (err) {
      setError("Failed to load page");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (url) {
      const parsedUrl = new URL(url);
      const hostParts = parsedUrl.hostname.split(".");
      const subdomain = hostParts.length > 2 ? hostParts[0] : null;
      fetchPage(subdomain);
    } else {
      setError("No URL provided");
      setLoading(false);
    }
  }, [url]);

  return (
    <shadow.div>
      <style>{normalizeTailwind(styles)}</style>
      {page && renderIntegrations(page.integrations)}
      <div
        className={cn("ds-widget max-w-[1280px] mx-auto rounded-md", colorMode)}
      >
        <div className={cn(colorTheme)}>
          {popup && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 z-50 rounded-full"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          )}
          {loading && (
            <div className="fixed inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          )}

          {!loading && error && (
            <div className="fixed inset-0 flex items-center justify-center text-red-600">
              <p>{error}</p>
            </div>
          )}
          {!loading && page && <MainComponent page={page} view={"resources"} />}
        </div>
      </div>
    </shadow.div>
  );
};
